import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
export interface SlotsContextValue {
  slots: Record<string, ReactNode>;
  getSlot: (key: string) => ReactNode | undefined;
}

const SlotsContext = createContext<SlotsContextValue | undefined>(undefined);

export interface SlotsProviderProps {
  children: ReactNode;
  slots?: Record<string, ReactNode>;
}

export function SlotsProvider({ children, slots = EMPTY_OBJECT }: SlotsProviderProps) {
  const value: SlotsContextValue = useMemo(
    () => ({
      slots,
      getSlot: (key: string) => slots[key],
    }),
    [slots],
  );

  return <SlotsContext.Provider value={value}>{children}</SlotsContext.Provider>;
}

export function useSlots(): SlotsContextValue {
  const context = useContext(SlotsContext);

  if (context === undefined) {
    throw new Error('useSlots must be used within a SlotsProvider');
  }

  return context;
}

export function useSlot(key: string): {
  slot: ReactNode | undefined;
} {
  const { getSlot } = useSlots();

  const slot = useMemo(() => getSlot(key), [getSlot, key]);

  return {
    slot,
  };
}

export default SlotsProvider;
